import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppState } from '../hooks/useAppState';
import type { Deck } from '../types';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Dialog } from '../components/ui/Dialog';
import DeckList from '../components/DeckList';
import DeckEditor from '../components/DeckEditor';
import {
  Plus,
  Search,
  FolderOpen,
  BookOpen,
  Grid3X3,
  List,
  Sparkles,
  AlertTriangle,
  Download,
} from 'lucide-react';

type ViewMode = 'grid' | 'list';
type FilterType = 'all' | 'favorites' | 'recent';

export const DecksPage: React.FC = () => {
  const { decks, addDeck, updateDeck, deleteDeck } = useAppState();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<FilterType>('all');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingDeck, setEditingDeck] = useState<Deck | null>(null);

  const filteredDecks = decks
    .filter((deck) => {
      if (filter === 'favorites') return deck.isFavorite;
      if (filter === 'recent') {
        const recentDate = new Date();
        recentDate.setDate(recentDate.getDate() - 7);
        return new Date(deck.updatedAt) > recentDate;
      }
      return true;
    })
    .filter(
      (deck) =>
        deck.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        deck.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        deck.tags.some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase()))
    )
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

  const handleCreateDeck = (deck: Deck) => {
    addDeck(deck);
    setShowCreateDialog(false);
  };

  const handleUpdateDeck = (deck: Deck) => {
    updateDeck(deck.id, deck);
    setEditingDeck(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <FolderOpen className="w-6 h-6" />
            My Decks
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {decks.length} deck{decks.length !== 1 ? 's' : ''}, {' '}
            {decks.reduce((acc, d) => acc + d.cards.length, 0)} cards
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
            leftIcon={viewMode === 'grid' ? <List className="w-4 h-4" /> : <Grid3X3 className="w-4 h-4" />}
          >
            {viewMode === 'grid' ? 'List' : 'Grid'}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => setShowCreateDialog(true)}
            leftIcon={<Plus className="w-4 h-4" />}
          >
            Create Deck
          </Button>
        </div>
      </div>

      {/* Local Storage Warning */}
      {decks.length > 0 && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-amber-800 dark:text-amber-200">
                Your decks are stored locally in your browser
              </h3>
              <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                Closing your browser or clearing browser data will delete your decks. If you plan to keep them, please export your decks through Settings to download them as a file.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/settings')}
              leftIcon={<Download className="w-4 h-4" />}
              className="shrink-0 border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/40"
            >
              Export
            </Button>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1">
          <Input
            placeholder="Search decks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            leftIcon={<Search className="w-4 h-4" />}
          />
        </div>
        <div className="flex gap-2">
          {(['all', 'favorites', 'recent'] as FilterType[]).map((f) => (
            <Button
              key={f}
              variant={filter === f ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => setFilter(f)}
            >
              {f === 'all' && 'All'}
              {f === 'favorites' && 'Favorites'}
              {f === 'recent' && 'Recent'}
            </Button>
          ))}
        </div>
      </div>

      {/* Decks */}
      {filteredDecks.length > 0 ? (
        <DeckList
          decks={filteredDecks}
          onSelectDeck={(deck) => {
            // Show deck detail or edit
            setEditingDeck(deck);
          }}
          onStudyDeck={(deck) => navigate(`/study?deck=${deck.id}`)}
          onEditDeck={(deck) => setEditingDeck(deck)}
          onDeleteDeck={(id) => {
            if (window.confirm('Are you sure you want to delete this deck?')) {
              deleteDeck(id);
            }
          }}
        />
      ) : (
        <div className="text-center py-16 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-2xl">
          <div className="w-16 h-16 mx-auto bg-blue-50 dark:bg-blue-900/30 rounded-full flex items-center justify-center mb-4">
            <BookOpen className="w-8 h-8 text-blue-600 dark:text-blue-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            {searchQuery ? 'No matching decks' : 'No decks yet'}
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4 max-w-md mx-auto">
            {searchQuery
              ? 'Try adjusting your search terms or filter settings.'
              : 'Create your first deck to start studying. Or generate one with AI!'}
          </p>
          {!searchQuery && (
            <div className="flex justify-center gap-3">
              <Button onClick={() => setShowCreateDialog(true)} leftIcon={<Plus className="w-4 h-4" />}>
                Create Deck
              </Button>
              <Button variant="secondary" leftIcon={<Sparkles className="w-4 h-4" />} onClick={() => navigate('/generate')}>
                Generate with AI
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        title="Create New Deck"
      >
        <DeckEditor
          onSave={handleCreateDeck}
          onCancel={() => setShowCreateDialog(false)}
        />
      </Dialog>

      <Dialog
        open={!!editingDeck}
        onClose={() => setEditingDeck(null)}
        title="Edit Deck"
      >
        {editingDeck && (
          <DeckEditor
            deck={editingDeck}
            onSave={handleUpdateDeck}
            onCancel={() => setEditingDeck(null)}
          />
        )}
      </Dialog>
    </div>
  );
};

export default DecksPage;
